import {
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useAppSelector } from '../../../redux/hook'
import Entity from '../../Entity/EntityList'
import { InTransitSettingsType, IShipment } from './interfaces'
import MapViewSkeleton from './MapViewSkeleton'
import {
    GoogleMap,
    InfoWindowF,
    Libraries,
    useJsApiLoader,
} from '@react-google-maps/api'
import { MarkerClusterer, Cluster } from '@googlemaps/markerclusterer'
import { Box, CircularProgress, Typography, IconButton } from '@mui/material'

import { useSearchParams } from 'react-router-dom'
import {
    Applications,
    IN_TRANSIT_TABS,
    INTRANSIT_STORAGE_KEYS,
    MapLayoutMode,
    TAB_PAGE_VIEW,
    URLSearchParamsKeys,
    greenMarkerIcon,
    googleMapOptions,
    USACenter,
    redMarkerIconUnread,
    redMarkerIconAcknowledged,
} from '../../../constants/constants'
import { fetchAllShipmentLocations } from '../../../services/coldChainServices'
import { formatDateTimeWithUserPreferences } from '../../../utils/DateAndTimeUtils'
import { getAddress } from '../../../CustomHooks/useGeoCache'
import {
    getAlarmStatusFilters,
    getColumnFilters,
    getShipmentStatusFilters,
    resolveMapLayout,
} from '../../../utils/IntransitUtils'
import { buildScopedKey } from '../../../utils/storageUtils'
import ViewEntity from '../../ViewEntity'
import {
    generateVerticalSplitClusterIcon,
    hasValidPosition,
} from '../../../utils/MapUtils'
import useEntityInstanceFiltersInit from '../../../CustomHooks/useEntityInstanceFiltersInit'
import {
    IColumn,
    IFilterValue,
    IGlobalSearchTextFilter,
} from '../../Entity/interfaces'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'
import MapControls from '../../Common/MapControls'
import MapFullscreenFilterOverlay from './MapFullscreenFilterOverlay'
import { getSelectedTimezoneLabel } from '../../../utils/TimezoneOptions'
import useDebounce from '../../../CustomHooks/useDebounce'
import {
    deriveColumnFiltersFromStore,
    getEntityTableFilters,
    getEntityTableGlobalSearchTextFilter,
} from '../../../utils/EntityUtils'
import {
    isCriticalAlarm,
    isAlarmFullyAcknowledged,
} from '../../../utils/MiscellaneousUtils'

const libraries: Libraries = ['places']

const MapView = (): ReactElement => {
    const [searchParams] = useSearchParams()
    const tabPageView =
        searchParams.get(URLSearchParamsKeys.pageView) ||
        TAB_PAGE_VIEW.LIST_VIEW
    const isInTransitMapAndListView = TAB_PAGE_VIEW.LIST_VIEW === tabPageView

    return isInTransitMapAndListView ? (
        <ShipmentsMapAndTableListing />
    ) : (
        <ViewEntity />
    )
}

const ShipmentsMapAndTableListing = () => {
    const { t } = useTranslation()
    const inTransitSlice = useAppSelector((state) => state.inTransit)
    const orgId = useAppSelector((state) => state.org.org?.id)
    const userId = useAppSelector((state) => state.user.user?.id)
    const { inTransitSettings } = inTransitSlice
    const {
        shipmentEntityTemplateId,
        displayAddressInTooltip,
        dateFilter,
        shipmentStatusAttributeId,
        alarmStatusAttributeId = 'alarmStatus',
        lastKnowAddressAttributeId = 'lastKnownAddress',
        lastReadingAttributeId = 'lastReading',
        defaultMapLayout = 'both',
        shipmentRefreshInterval = '',
    } = inTransitSettings || ({} as InTransitSettingsType)
    const allTableFilters = useAppSelector(
        (state) => state.entity.allTableFilters,
    )
    const columnVisibilityList = useAppSelector(
        (state) => state.entity.entityColumns,
    )
    const isFetchingColumn = useAppSelector(
        (state) => state.entity.isFetchingColumn,
    )
    const columnVisibilityTemplateId = useAppSelector(
        (state) => state.entity.columnVisibilityTemplateId,
    )
    const allAttributesFilters: Record<string, IFilterValue> =
        getEntityTableFilters(
            allTableFilters,
            shipmentEntityTemplateId,
            Applications.inTransit.id,
            IN_TRANSIT_TABS.MAP_VIEW.toString(),
        )

    const isInitialLoad = useRef(true)
    /**
     * In EntityList/index.tsx file we debounce individual attribute filters.
     * Here we debounce the combined attribute filters. This is done because in
     * EntityList/index.tsx file we should show filter count immediately and the columnFilters
     * should be given instatenously to the setColumnFiltersHandler for state update.
     */
    const debounceValue = isInitialLoad.current ? 10 : 1000
    const debouncedAllAttributesFilters = useDebounce(
        allAttributesFilters,
        debounceValue,
    )
    const gloalSearchText: IGlobalSearchTextFilter | undefined =
        getEntityTableGlobalSearchTextFilter(
            allTableFilters,
            shipmentEntityTemplateId,
            Applications.inTransit.id,
            IN_TRANSIT_TABS.MAP_VIEW.toString(),
        )
    const searchValueFromStore = gloalSearchText?.searchText.trim() || ''
    const selectedColumnFromStore = gloalSearchText?.column || null
    const [selectedMarker, setSelectedMarker] = useState<IShipment | null>(null)
    const [address, setAddress] = useState('')
    const [isLoadingAddress, setIsLoadingAddress] = useState(false)
    const [searchValue, setSearchValue] = useState(searchValueFromStore)
    const [selectedColumn, setSelectedColumn] = useState<IColumn | null>(
        selectedColumnFromStore,
    )
    const [shipments, setShipments] = useState<IShipment[]>([])
    const [totalCountWithoutFilters, setTotalCountWithoutFilters] = useState(0)
    const [, setSearchParams] = useSearchParams()
    const userLocalization = useAppSelector(
        (state) => state.user.user?.preferences?.localization,
    )

    const [sortParams, setSortParams] = useState<{
        orderAttribute: string
        orderBy: 'ASC' | 'DESC'
    }>({
        orderAttribute: '',
        orderBy: 'ASC',
    })

    const columnFilters = useMemo(() => {
        return deriveColumnFiltersFromStore(
            debouncedAllAttributesFilters,
            columnVisibilityList,
        )
    }, [debouncedAllAttributesFilters, columnVisibilityList])
    const isGlobalSearchColumnInitialized =
        gloalSearchText?.column !== undefined

    // get user preferred timezone
    const preferredTimeZone: string =
        userLocalization?.['timeZone'] ||
        Intl.DateTimeFormat().resolvedOptions().timeZone

    const maxZoom = 21
    const minZoom = 1
    const CITY_LEVEL_ZOOM = 10
    const MIN_BOUNDS_DELTA = 0.01

    const [mapLayout, setMapLayout] = useState<MapLayoutMode>(() =>
        resolveMapLayout(defaultMapLayout, orgId, userId),
    )

    const [mapZoom, setMapZoom] = useState(minZoom)
    const [mapReady, setMapReady] = useState(false)

    const handleMapLayoutChange = useCallback(
        (next: MapLayoutMode) => {
            if (document.fullscreenElement) {
                document.exitFullscreen()
            }
            localStorage.setItem(
                buildScopedKey(INTRANSIT_STORAGE_KEYS.mapLayout, orgId, userId),
                next,
            )
            setMapLayout(next)
        },
        [orgId, userId],
    )

    // Reload the persisted layout when the active org or user changes without a
    // full remount, so each org/user sees its own saved preference.
    useEffect(() => {
        setMapLayout(resolveMapLayout(defaultMapLayout, orgId, userId))
    }, [defaultMapLayout, orgId, userId])

    const mapContainerRef = useRef<HTMLDivElement>(null)
    const [isFullscreen, setIsFullscreen] = useState(false)

    const handleToggleFullscreen = useCallback(() => {
        if (document.fullscreenElement) {
            document.exitFullscreen()
        } else if (mapContainerRef.current) {
            mapContainerRef.current.requestFullscreen()
        }
    }, [])

    useEffect(() => {
        const onFsChange = () => {
            setIsFullscreen(!!document.fullscreenElement)
            // Trigger map resize after fullscreen transition
            setTimeout(() => {
                if (mapRef.current) {
                    google.maps.event.trigger(mapRef.current, 'resize')
                    if (lastFitBoundsRef.current) {
                        mapRef.current.fitBounds(lastFitBoundsRef.current)
                    }
                }
            }, 100)
        }
        document.addEventListener('fullscreenchange', onFsChange)
        return () =>
            document.removeEventListener('fullscreenchange', onFsChange)
    }, [])

    const searchableColumns = useMemo(
        () =>
            columnVisibilityList
                ?.filter((column) => column?.searchable)
                .map((i) => ({
                    ...i,
                    columnName: i.columnName,
                })) ?? [],
        [columnVisibilityList],
    )

    const dateFilterLabel = useMemo(
        () => columnVisibilityList.find((i) => i.id === dateFilter)?.name || '',
        [columnVisibilityList, dateFilter],
    )

    const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        libraries,
    })

    useEntityInstanceFiltersInit(
        shipmentEntityTemplateId,
        Applications.inTransit.id,
        IN_TRANSIT_TABS.MAP_VIEW.toString(),
        true,
    )

    const mapRef = useRef<google.maps.Map | null>(null)
    const clustererRef = useRef<MarkerClusterer | null>(null)
    const markerListeners = useRef<google.maps.MapsEventListener[]>([])
    const lastFitBoundsRef = useRef<google.maps.LatLngBounds | null>(null)
    const lastCenteredPositionRef = useRef<google.maps.LatLngLiteral | null>(
        null,
    )

    const shouldDisplayAddress = displayAddressInTooltip === 'true'

    const lastKnownAddress = useMemo(() => {
        return selectedMarker?.attributes.find(
            (attr) => attr.id === lastKnowAddressAttributeId,
        )?.value as string | undefined
    }, [selectedMarker, lastKnowAddressAttributeId])

    const lastReading = useMemo(() => {
        return selectedMarker?.attributes.find(
            (attr) => attr.id === lastReadingAttributeId,
        )?.value as string | undefined
    }, [selectedMarker, lastReadingAttributeId])

    // show marker info window
    const handleShowMarkerInfo = useCallback(
        (shipmentUid: string) => {
            const marker = shipments.find(
                (shipment) =>
                    shipment?.shipmentName === shipmentUid ||
                    shipment?.shipmentId === shipmentUid ||
                    shipment?.shipmentInstanceId === shipmentUid,
            )
            if (!marker) return

            setSelectedMarker(marker)
        },
        [shipments],
    )

    const onMarkerClick = useCallback(
        (shipmentInstanceId: string) => {
            setSearchParams({
                [URLSearchParamsKeys.tabIndex]:
                    IN_TRANSIT_TABS.MAP_VIEW.toString(),
                [URLSearchParamsKeys.pageView]: TAB_PAGE_VIEW.DETAILS_VIEW,
                [URLSearchParamsKeys.applicationId]: Applications.inTransit.id,
                [URLSearchParamsKeys.instanceId]: shipmentInstanceId,
                [URLSearchParamsKeys.templateId]: shipmentEntityTemplateId,
            })
        },
        [setSearchParams, shipmentEntityTemplateId],
    )

    const attributesInAlarm = useMemo(() => {
        const highCritical =
            selectedMarker?.alarmStatus?.['High Critical'] || []
        const lowCritical = selectedMarker?.alarmStatus?.['Low Critical'] || []
        const critical = selectedMarker?.alarmStatus?.['Critical'] || []

        const attributes = [...highCritical, ...lowCritical, ...critical].map(
            (item) => item.displayName,
        )

        return [...new Set(attributes)]
    }, [selectedMarker])

    const alarmAttributesString = attributesInAlarm.join(', ') || ''

    const fetchAddress = useCallback(
        async (selectedMarker: IShipment) => {
            const lat = selectedMarker.position?.lat
            const lng = selectedMarker.position?.lng
            if (typeof lat !== 'number' || typeof lng !== 'number') {
                setAddress('N/A')
                return
            }
            setIsLoadingAddress(true)
            const addr = (await getAddress(lat, lng, GOOGLE_MAPS_API_KEY)) || ''
            setAddress(addr)
            setIsLoadingAddress(false)
        },
        [GOOGLE_MAPS_API_KEY],
    )

    const shipmentStatusFilter = getShipmentStatusFilters(
        debouncedAllAttributesFilters,
        shipmentStatusAttributeId,
    )
    const alarmStatusFilter = getAlarmStatusFilters(
        debouncedAllAttributesFilters,
        alarmStatusAttributeId,
    )
    const fromDate =
        (debouncedAllAttributesFilters[dateFilter]?.startDate as string) || ''
    const toDate =
        (debouncedAllAttributesFilters[dateFilter]?.endDate as string) || ''
    /**
     * Global Search Filer is initialized in EntityListSearch.tsx file separtely
     * so we need to debounce it here
     */
    const debouncedIsGlobalSearchColumnInitialized = useDebounce(
        isGlobalSearchColumnInitialized,
        debounceValue,
    )

    const loadShipmentsOnMap = useCallback(
        async ({ shouldFitBounds }: { shouldFitBounds: boolean }) => {
            if (!mapRef.current || !clustererRef.current) {
                return
            }
            const filters = getColumnFilters(
                columnFilters,
                columnVisibilityList,
            )
            const shipmentsResult = await fetchAllShipmentLocations({
                shipmentStatus: shipmentStatusFilter,
                alarmStatus: alarmStatusFilter,
                orgId: orgId || '',
                templateId: shipmentEntityTemplateId,
                searchText: searchValue,
                searchAttributeName: searchValue
                    ? selectedColumn?.attributeName ?? ''
                    : '',
                searchAttributeType: searchValue
                    ? selectedColumn?.attributeType ?? ''
                    : '',
                filterByActualArrivalDate: true,
                dateFilterAttributeId: dateFilter,
                fromDate,
                toDate,
                currentLocationBounds: null,
                localTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                filters,
                entitySearch: !!searchValue,
                orderAttribute: sortParams.orderAttribute,
                orderBy: sortParams.orderBy,
            })
            if (!mapRef.current || !clustererRef.current) {
                return
            }
            setShipments(shipmentsResult)

            const bounds = new google.maps.LatLngBounds()
            const validMarkers = shipmentsResult.filter(hasValidPosition)

            if (validMarkers.length === 0) {
                lastFitBoundsRef.current = null
                lastCenteredPositionRef.current = null
            }

            clustererRef.current.clearMarkers()
            markerListeners.current.forEach((listener) => {
                google.maps.event.removeListener(listener)
            })
            markerListeners.current = []

            const markersForCluster = validMarkers.map((marker) => {
                bounds.extend(
                    new google.maps.LatLng(
                        marker.position.lat,
                        marker.position.lng,
                    ),
                )

                const isCritical = isCriticalAlarm(marker?.alarmStatus)
                // UBQ-5337: acknowledged keeps the red alarm colour and gains the blue checkmark —
                // it means "seen and owned", not resolved, so the pin must never flip to green.
                const isAcknowledged = isAlarmFullyAcknowledged(
                    marker?.alarmStatus,
                )

                const newMarker = new google.maps.Marker({
                    position: marker.position,
                    clickable: true,
                    icon: {
                        url: isCritical
                            ? isAcknowledged
                                ? redMarkerIconAcknowledged
                                : redMarkerIconUnread
                            : greenMarkerIcon,
                    },
                    title: '',
                })
                const listener = google.maps.event.addListener(
                    newMarker,
                    'click',
                    () => {
                        setSelectedMarker(marker)
                    },
                )
                markerListeners.current.push(listener)
                return newMarker
            })

            clustererRef.current.addMarkers(markersForCluster)

            if (!shouldFitBounds || validMarkers.length === 0) {
                return
            }

            const ne = bounds.getNorthEast()
            const sw = bounds.getSouthWest()
            if (!ne || !sw) {
                return
            }

            const latDiff = Math.abs(ne.lat() - sw.lat())
            const lngDiff = Math.abs(ne.lng() - sw.lng())
            const isTooClose =
                latDiff < MIN_BOUNDS_DELTA && lngDiff < MIN_BOUNDS_DELTA

            if (validMarkers.length === 1 || isTooClose) {
                const center = validMarkers[0].position
                const centerLatLng = new google.maps.LatLng(
                    center.lat,
                    center.lng,
                )
                lastFitBoundsRef.current = null
                lastCenteredPositionRef.current = {
                    lat: center.lat,
                    lng: center.lng,
                }
                mapRef.current.setCenter(centerLatLng)
                mapRef.current.setZoom(CITY_LEVEL_ZOOM)
                setMapZoom(CITY_LEVEL_ZOOM)
            } else {
                const clonedBounds = new google.maps.LatLngBounds(
                    bounds.getSouthWest(),
                    bounds.getNorthEast(),
                )
                lastFitBoundsRef.current = clonedBounds
                const centerLatLng = bounds.getCenter()
                lastCenteredPositionRef.current = centerLatLng
                    ? centerLatLng.toJSON()
                    : null
                mapRef.current.fitBounds(bounds)
                window.setTimeout(() => {
                    if (mapRef.current) {
                        const latestZoom = mapRef.current.getZoom()
                        setMapZoom(
                            typeof latestZoom === 'number'
                                ? latestZoom
                                : minZoom,
                        )
                    }
                }, 0)
            }
        },
        [
            searchValue,
            fromDate,
            toDate,
            selectedColumn,
            orgId,
            dateFilter,
            columnFilters,
            shipmentEntityTemplateId,
            shipmentStatusFilter,
            alarmStatusFilter,
            columnVisibilityList,
            sortParams,
        ],
    )

    const renderer = useMemo(
        () => ({
            render: (cluster: Cluster) => {
                const count = cluster.count
                const position = cluster.position
                const markers = cluster.markers as google.maps.Marker[]
                // UBQ-5337: the cluster's red/green split counts by icon URL, so EVERY in-alarm pin
                // variant must be listed here. Miss one and acknowledged shipments silently fall into
                // the "normal" half of the cluster badge.
                const criticalPinUrls: string[] = [
                    redMarkerIconAcknowledged,
                    redMarkerIconUnread,
                ]
                const totalCritical = markers.filter((marker) =>
                    criticalPinUrls.includes(
                        (marker.getIcon() as google.maps.Icon)?.url ?? '',
                    ),
                ).length
                const totalNormal = markers.length - totalCritical

                const marker = new google.maps.Marker({
                    position,
                    icon: {
                        url: `data:image/svg+xml;base64,${generateVerticalSplitClusterIcon(totalCritical, totalNormal).base64}`,
                        scaledSize: new google.maps.Size(35, 35),
                    },
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                })
                return marker
            },
        }),
        [],
    )

    const onLoad = useCallback(
        (map: google.maps.Map) => {
            mapRef.current = map
            clustererRef.current = new MarkerClusterer({ map, renderer })

            lastFitBoundsRef.current = null
            lastCenteredPositionRef.current = null
            setMapReady(true)
        },
        [renderer],
    )

    const onUnmount = useCallback(() => {
        mapRef.current = null
        clustererRef.current = null
        // Remove all marker click listeners
        markerListeners.current.forEach((listener) => {
            google.maps.event.removeListener(listener)
        })
        markerListeners.current = []
        lastFitBoundsRef.current = null
        lastCenteredPositionRef.current = null
        setMapReady(false)
    }, [])

    useEffect(() => {
        if (mapRef.current && mapReady) {
            const timer = setTimeout(() => {
                google.maps.event.trigger(mapRef.current!, 'resize')
            }, 350)
            return () => clearTimeout(timer)
        }
        return undefined
    }, [mapLayout, mapReady])

    const handleZoomReset = useCallback(() => {
        if (!mapRef?.current) {
            return
        }

        if (lastFitBoundsRef.current) {
            mapRef.current.fitBounds(lastFitBoundsRef.current)
            window.setTimeout(() => {
                if (mapRef.current) {
                    const latestZoom = mapRef.current.getZoom()
                    setMapZoom(
                        typeof latestZoom === 'number' ? latestZoom : minZoom,
                    )
                }
            }, 0)
            return
        }

        if (lastCenteredPositionRef.current) {
            const { lat, lng } = lastCenteredPositionRef.current
            mapRef.current.setCenter(new google.maps.LatLng(lat, lng))
            mapRef.current.setZoom(CITY_LEVEL_ZOOM)
            setMapZoom(CITY_LEVEL_ZOOM)
            return
        }

        mapRef.current.setZoom(minZoom)
        setMapZoom(minZoom)
    }, [CITY_LEVEL_ZOOM, minZoom])

    const handleZoomIn = useCallback(() => {
        if (mapRef?.current) {
            const currentCenter = mapRef.current.getCenter()
            const newZoom = Math.min(
                (mapRef?.current?.getZoom() || 1) + 1,
                maxZoom,
            )
            mapRef.current.setZoom(newZoom)
            if (currentCenter) {
                mapRef.current.setCenter(currentCenter)
            }
            setMapZoom(newZoom)
        }
    }, [maxZoom])

    const handleZoomOut = useCallback(() => {
        if (mapRef?.current) {
            const currentCenter = mapRef.current.getCenter()
            const newZoom = Math.max(
                (mapRef?.current?.getZoom() || 1) - 1,
                minZoom,
            )
            mapRef.current.setZoom(newZoom)
            if (currentCenter) {
                mapRef.current.setCenter(currentCenter)
            }
            setMapZoom(newZoom)
        }
    }, [minZoom])

    // Update zoom state when user zooms via map gestures
    const handleZoomChanged = useCallback(() => {
        if (mapRef?.current) {
            setMapZoom(mapRef?.current?.getZoom() ?? minZoom)
        }
    }, [])

    useEffect(() => {
        if (
            mapReady &&
            !isFetchingColumn &&
            columnVisibilityTemplateId === shipmentEntityTemplateId &&
            columnVisibilityList.length > 0 &&
            debouncedIsGlobalSearchColumnInitialized
        ) {
            loadShipmentsOnMap({ shouldFitBounds: true })
            isInitialLoad.current = false
        }
    }, [
        mapReady,
        loadShipmentsOnMap,
        isFetchingColumn,
        columnVisibilityTemplateId,
        shipmentEntityTemplateId,
        columnVisibilityList,
        debouncedIsGlobalSearchColumnInitialized,
        preferredTimeZone,
    ])

    /**
     * Auto-refresh shipments on a scheduled interval configured in Settings
     * (shipmentRefreshInterval, in minutes). We pass shouldFitBounds: false so
     * the refresh only fetches the latest shipments without resetting the zoom
     * level or re-fitting the map bounds.
     */
    useEffect(() => {
        const intervalMinutes = Number(shipmentRefreshInterval)
        if (!mapReady || !intervalMinutes || intervalMinutes <= 0) {
            return undefined
        }
        const intervalId = window.setInterval(
            () => {
                loadShipmentsOnMap({ shouldFitBounds: true })
            },
            intervalMinutes * 60 * 1000,
        )
        return () => window.clearInterval(intervalId)
    }, [shipmentRefreshInterval, mapReady, loadShipmentsOnMap])

    useEffect(() => {
        if (!selectedMarker || !shouldDisplayAddress) {
            return
        }
        fetchAddress(selectedMarker)
    }, [selectedMarker, shouldDisplayAddress, fetchAddress])

    const closeInfoBox = useCallback(() => {
        setSelectedMarker(null)
    }, [])

    // Split size is intentionally NOT org/user-scoped — it's a single shared
    // preference across all orgs and users on this browser.
    const [tablePercent, setTablePercent] = useState<number>(() => {
        try {
            const saved = localStorage.getItem(
                INTRANSIT_STORAGE_KEYS.mapSplitSizes,
            )
            if (saved) {
                const n = JSON.parse(saved)
                if (typeof n === 'number' && n >= 10 && n <= 90) return n
            }
        } catch {
            // localStorage unavailable/unparseable (e.g. private mode) — use default
        }
        return 66
    })

    // Keep a ref to the latest tablePercent so the mouseup closure can persist
    // the final value without needing it as a useCallback dependency.
    const tablePercentRef = useRef(tablePercent)
    tablePercentRef.current = tablePercent

    const outerContainerRef = useRef<HTMLDivElement>(null)

    const handleDragStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        const container = outerContainerRef.current
        if (!container) return

        const onMouseMove = (moveEvent: MouseEvent) => {
            const rect = container.getBoundingClientRect()
            const pct = ((moveEvent.clientY - rect.top) / rect.height) * 100
            setTablePercent(Math.min(80, Math.max(20, pct)))
        }

        const onMouseUp = () => {
            document.removeEventListener('mousemove', onMouseMove)
            document.removeEventListener('mouseup', onMouseUp)
            try {
                localStorage.setItem(
                    INTRANSIT_STORAGE_KEYS.mapSplitSizes,
                    JSON.stringify(tablePercentRef.current),
                )
            } catch {
                // localStorage write failed (e.g. private mode/quota) — ignore
            }
        }

        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
    }, [])

    // entityPanel is memoized so Entity (which is React.memo'd) only re-renders
    // when its props actually change, not on unrelated parent re-renders.
    const entityPanel = useMemo(
        () => (
            <Entity
                tabIndex={IN_TRANSIT_TABS.MAP_VIEW}
                applicationId={Applications.inTransit.id}
                templateId={shipmentEntityTemplateId}
                isInTransitMapView={true}
                mapLayout={mapLayout}
                onMapLayoutChange={handleMapLayoutChange}
                setSelectedColumn={setSelectedColumn}
                setSearchText={setSearchValue}
                onShowMarkerInfo={handleShowMarkerInfo}
                onTotalCountWithoutFiltersChange={setTotalCountWithoutFilters}
                sortParams={sortParams}
                setSortParams={setSortParams}
            />
        ),
        [
            shipmentEntityTemplateId,
            mapLayout,
            handleMapLayoutChange,
            setSelectedColumn,
            setSearchValue,
            handleShowMarkerInfo,
            sortParams,
            setSortParams,
            setTotalCountWithoutFilters,
        ],
    )

    if (!isLoaded) {
        return <MapViewSkeleton />
    }

    const mapPanel = (
        <>
            <GoogleMap
                mapContainerStyle={{
                    width: '100%',
                    height: '100%',
                }}
                center={USACenter} // Set default center
                zoom={mapZoom}
                onLoad={onLoad}
                onClick={closeInfoBox}
                onUnmount={onUnmount}
                options={{
                    ...googleMapOptions,
                    zoomControl: false, // Hide default zoom controls
                    fullscreenControl: false, // Using custom layout controls instead
                }}
                onZoomChanged={handleZoomChanged}
            >
                {selectedMarker?.position && (
                    <InfoWindowF
                        key={selectedMarker.shipmentInstanceId}
                        position={{
                            lat: selectedMarker.position.lat,
                            lng: selectedMarker.position.lng,
                        }}
                        onCloseClick={() => setSelectedMarker(null)}
                        options={{
                            disableAutoPan: false,
                            pixelOffset: new google.maps.Size(0, -30),
                        }}
                    >
                        <Box
                            sx={{
                                minWidth: '200px',
                                maxWidth: '300px',
                            }}
                        >
                            <style>
                                {`
                                        .gm-ui-hover-effect {
                                            display: none !important;
                                        }
                                        .gm-style-iw-ch {
                                            display: none !important;
                                        }
                                    `}
                            </style>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mt: 0.5,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 650,
                                        fontSize: '14px',
                                        color: '#1976d2',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() =>
                                        onMarkerClick(
                                            selectedMarker.shipmentInstanceId,
                                        )
                                    }
                                >
                                    {selectedMarker.shipmentName}
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => setSelectedMarker(null)}
                                    sx={{ p: 0.5 }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            {alarmAttributesString && (
                                <Box>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '13px',
                                            mr: 1,
                                        }}
                                    >
                                        {t('alarm')}:
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '13px',
                                        }}
                                    >
                                        {alarmAttributesString}
                                    </Typography>
                                </Box>
                            )}
                            {lastKnownAddress && (
                                <Box>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '13px',
                                            mr: 1,
                                        }}
                                    >
                                        {t('lastKnownAddress')}:
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '12px',
                                        }}
                                    >
                                        {lastKnownAddress}
                                    </Typography>
                                </Box>
                            )}
                            {lastReading && (
                                <Box>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '13px',
                                            mr: 1,
                                        }}
                                    >
                                        {t('lastReading')}:
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '12px',
                                        }}
                                    >
                                        {lastReading}
                                    </Typography>
                                </Box>
                            )}
                            {selectedMarker.shipmentEstimatedDate && (
                                <Box>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '13px',
                                            mr: 1,
                                        }}
                                    >
                                        {t('eta')}:
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '12px',
                                        }}
                                    >
                                        {formatDateTimeWithUserPreferences(
                                            selectedMarker.shipmentEstimatedDate,
                                            userLocalization,
                                        )}{' '}
                                        {getSelectedTimezoneLabel(
                                            preferredTimeZone,
                                        )}
                                    </Typography>
                                </Box>
                            )}
                            {shouldDisplayAddress && (
                                <Box>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: '13px',
                                            mr: 1,
                                        }}
                                    >
                                        {t('address')}:
                                    </Typography>
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '12px',
                                        }}
                                    >
                                        {isLoadingAddress ? (
                                            <CircularProgress size={12} />
                                        ) : (
                                            address || 'N/A'
                                        )}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </InfoWindowF>
                )}
            </GoogleMap>
            {isLoaded && (
                <MapControls
                    onZoomIn={handleZoomIn}
                    onZoomOut={handleZoomOut}
                    onZoomReset={handleZoomReset}
                    isZoomInDisabled={mapZoom >= maxZoom}
                    isZoomOutDisabled={mapZoom <= minZoom}
                    isFullscreen={isFullscreen}
                    onToggleFullscreen={handleToggleFullscreen}
                />
            )}
            {isFullscreen && (
                <MapFullscreenFilterOverlay
                    templateId={shipmentEntityTemplateId}
                    searchableColumns={searchableColumns}
                    setSearchValue={setSearchValue}
                    setSelectedColumn={setSelectedColumn}
                    dateFilterAttributeId={dateFilter}
                    dateFilterLabel={dateFilterLabel}
                    totalCount={shipments.length}
                    totalCountWithoutFilters={totalCountWithoutFilters}
                />
            )}
        </>
    )

    // Single stable layout — GoogleMap is always mounted at the same tree
    // position so it never unmounts/remounts when switching layout modes.
    // CSS controls size and visibility instead of conditional rendering.
    return (
        <Box
            ref={outerContainerRef}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
            }}
        >
            {/* Entity panel — always mounted, height driven by CSS */}
            <Box
                sx={{
                    overflow: 'hidden',
                    flexShrink: 0,
                    ...(mapLayout === 'table-only' && {
                        flex: 1,
                        flexShrink: undefined,
                    }),
                    ...(mapLayout === 'both' && {
                        height: `${tablePercent}%`,
                    }),
                }}
            >
                {entityPanel}
            </Box>

            {/* Drag separator — only visible in 'both' mode */}
            {mapLayout === 'both' && (
                <Box
                    onMouseDown={handleDragStart}
                    sx={{
                        height: '6px',
                        flexShrink: 0,
                        background: '#e0e0e0',
                        cursor: 'row-resize',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover, &:active': {
                            background: '#1976d2',
                        },
                        transition: 'background 0.15s ease',
                        userSelect: 'none',
                    }}
                >
                    <Box
                        sx={{
                            width: '32px',
                            height: '3px',
                            borderRadius: '2px',
                            background: '#bdbdbd',
                        }}
                    />
                </Box>
            )}

            {/* Map panel — ALWAYS mounted. Hidden via CSS in table-only so
                GoogleMap never unmounts and never needs an expensive reinit. */}
            <Box
                ref={mapContainerRef}
                sx={{
                    position: 'relative',
                    ...(mapLayout === 'table-only'
                        ? {
                              visibility: 'hidden',
                              height: 0,
                              overflow: 'hidden',
                              flexShrink: 0,
                          }
                        : {
                              flex: 1,
                              minHeight: 0,
                          }),
                    ...(isFullscreen && { background: '#fff' }),
                }}
            >
                {mapPanel}
            </Box>
        </Box>
    )
}

export default MapView
